import { type KeyboardEvent, useState } from "react";
import { ArrowUp, Square } from "lucide-react";

interface PlatformComposerProps {
  isRunning: boolean;
  // Set while the panel re-syncs with the server after a run ends.
  disabled?: boolean;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}

// The help panel's composer: a growing textarea and a send button, without the
// session composer's attachments, skills or connectors.
export function PlatformComposer({
  isRunning,
  disabled = false,
  onSubmit,
  onCancel,
}: PlatformComposerProps) {
  const [text, setText] = useState("");
  const canSubmit = text.trim().length > 0 && !isRunning && !disabled;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(text.trim());
    setText("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="flex items-end gap-1.5 rounded-2xl border border-border bg-background p-1.5 transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
    >
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        // Mounts only when the panel opens, so typing can start straight away.
        autoFocus
        placeholder="Ask a question..."
        aria-label="Ask a question"
        className="max-h-28 min-h-8 flex-1 resize-none bg-transparent px-2 py-1.5 text-[13px] text-foreground outline-none field-sizing-content placeholder:text-muted-foreground"
      />
      <button
        type={isRunning ? "button" : "submit"}
        onClick={isRunning ? onCancel : undefined}
        disabled={!isRunning && !canSubmit}
        aria-label={isRunning ? "Stop generation" : "Send"}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isRunning ? (
          <Square className="size-3 fill-current" aria-hidden />
        ) : (
          <ArrowUp className="size-4" aria-hidden />
        )}
      </button>
    </form>
  );
}
